// ==================== EXPORT GROUP MEMBERS MODAL ====================

import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Download,
  FileSpreadsheet,
  FileJson,
  Loader2,
  CheckCircle,
  Users,
} from 'lucide-react';
import { Modal, Button, Checkbox, Badge, Card } from '../../../components/ui';
import { contactsService, type GroupMember } from '../../../services/contacts.service';
import type { ContactGroup } from '../../../types';
import { getCountryByCode } from '../../../constants/countries.constants';

// ==================== TYPES ====================

type ExportFormat = 'csv' | 'json';

interface ExportGroupMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: ContactGroup | null;
}

interface ExportField {
  key: string;
  label: string;
  checked: boolean;
}

// ==================== CONSTANTS ====================

const DEFAULT_FIELDS: ExportField[] = [
  { key: 'phoneNumber', label: 'Phone Number', checked: true },
  { key: 'name', label: 'Name', checked: true },
  { key: 'email', label: 'Email', checked: true },
  { key: 'country', label: 'Country', checked: true },
  { key: 'optedIn', label: 'Opted In Status', checked: true },
  { key: 'addedAt', label: 'Added to Group Date', checked: true },
  { key: 'createdAt', label: 'Contact Created Date', checked: false },
];

// ==================== MAIN COMPONENT ====================

const ExportGroupMembersModal = ({
  isOpen,
  onClose,
  group,
}: ExportGroupMembersModalProps) => {
  // State
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [fields, setFields] = useState<ExportField[]>(DEFAULT_FIELDS);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Get member count
  const memberCount = group?._count?.members || 0;

  // Toggle field selection
  const toggleField = (key: string) => {
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, checked: !f.checked } : f))
    );
  };

  // Select/deselect all fields
  const toggleAllFields = (checked: boolean) => {
    setFields((prev) => prev.map((f) => ({ ...f, checked })));
  };

  // Count selected fields
  const selectedFieldCount = fields.filter((f) => f.checked).length;

  // Fetch all members (paginate through all pages)
  const fetchAllMembers = async (): Promise<GroupMember[]> => {
    if (!group) return [];
    
    const allMembers: GroupMember[] = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      const response = await contactsService.getGroupMembers(group.id, { 
        page, 
        limit: 100 
      });
      
      allMembers.push(...response.items);
      
      if (page >= response.meta.totalPages) {
        hasMore = false;
      } else {
        page++;
      }
    }
    
    return allMembers;
  };

  // Handle export
  const handleExport = async () => {
    if (!group) return;
    
    setIsExporting(true);
    setExportSuccess(false);

    try {
      const members = await fetchAllMembers();

      // Get selected field keys
      const selectedFields = fields.filter((f) => f.checked).map((f) => f.key);

      if (format === 'csv') {
        exportAsCSV(members, selectedFields);
      } else {
        exportAsJSON(members, selectedFields);
      }

      setExportSuccess(true);
      setTimeout(() => {
        setExportSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to export group members:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export as CSV
  const exportAsCSV = (members: GroupMember[], selectedFields: string[]) => {
    const headers = selectedFields.map((key) => {
      const field = fields.find((f) => f.key === key);
      return field?.label || key;
    });

    const rows = members.map((member) => {
      return selectedFields.map((key) => {
        if (key === 'addedAt') {
          return member.addedAt ? new Date(member.addedAt).toISOString() : '';
        }
        
        const value = member.contact[key as keyof typeof member.contact];
        
        if (key === 'optedIn') {
          return value ? 'Yes' : 'No';
        }
        if (key === 'country' && typeof value === 'string') {
          const country = getCountryByCode(value);
          return country ? country.name : value;
        }
        if (key === 'createdAt' && value) {
          return new Date(value as string).toISOString();
        }
        
        return value || '';
      });
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    downloadFile(csv, 'text/csv', 'csv');
  };

  // Export as JSON
  const exportAsJSON = (members: GroupMember[], selectedFields: string[]) => {
    const filteredData = members.map((member) => {
      const filtered: Record<string, unknown> = {};
      
      selectedFields.forEach((key) => {
        if (key === 'addedAt') {
          filtered[key] = member.addedAt;
          return;
        }
        
        const value = member.contact[key as keyof typeof member.contact];
        
        if (key === 'country' && typeof value === 'string') {
          const country = getCountryByCode(value);
          filtered[key] = country ? { code: value, name: country.name } : value;
        } else {
          filtered[key] = value;
        }
      });
      
      return filtered;
    });

    const json = JSON.stringify(filteredData, null, 2);
    downloadFile(json, 'application/json', 'json');
  };

  // Download file helper
  const downloadFile = (content: string, mimeType: string, extension: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const groupName = group?.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase() || 'group';
    a.download = `${groupName}-members-${new Date().toISOString().split('T')[0]}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle close and reset
  const handleClose = () => {
    setFormat('csv');
    setFields(DEFAULT_FIELDS);
    setExportSuccess(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Export Group Members"
      subtitle={group ? `Export members from "${group.name}"` : 'Export group members'}
      showCloseButton
    >
      <div className="p-6 space-y-6">
        {/* Success State */}
        {exportSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-8"
          >
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-white font-medium">Export Complete!</p>
            <p className="text-sm text-gray-400">Your download should start automatically</p>
          </motion.div>
        ) : (
          <>
            {/* Group Info */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${group?.color || '#f97316'}15` }}
              >
                <Users
                  className="w-6 h-6"
                  style={{ color: group?.color || '#f97316' }}
                />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-medium">{group?.name}</h4>
                <p className="text-sm text-gray-400">
                  {memberCount} member{memberCount !== 1 ? 's' : ''} will be exported
                </p>
              </div>
            </div>

            {/* Format Selection */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-white">Export Format</h4>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormat('csv')}
                  className={`flex-1 p-4 rounded-lg border transition-colors ${
                    format === 'csv'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <FileSpreadsheet className={`w-6 h-6 mx-auto mb-2 ${format === 'csv' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${format === 'csv' ? 'text-orange-400' : 'text-white'}`}>CSV</p>
                  <p className="text-xs text-gray-500 mt-1">Spreadsheet compatible</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormat('json')}
                  className={`flex-1 p-4 rounded-lg border transition-colors ${
                    format === 'json'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <FileJson className={`w-6 h-6 mx-auto mb-2 ${format === 'json' ? 'text-orange-400' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${format === 'json' ? 'text-orange-400' : 'text-white'}`}>JSON</p>
                  <p className="text-xs text-gray-500 mt-1">Developer friendly</p>
                </button>
              </div>
            </div>

            {/* Field Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-white">Fields to Export</h4>
                <div className="flex items-center gap-2">
                  <Badge variant="default">{selectedFieldCount} selected</Badge>
                  <button
                    type="button"
                    onClick={() => toggleAllFields(selectedFieldCount < fields.length)}
                    className="text-xs text-orange-400 hover:text-orange-300"
                  >
                    {selectedFieldCount === fields.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>
              
              <Card className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {fields.map((field) => (
                    <label
                      key={field.key}
                      className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors"
                    >
                      <Checkbox
                        checked={field.checked}
                        onChange={() => toggleField(field.key)}
                      />
                      <span className={`text-sm ${field.checked ? 'text-white' : 'text-gray-400'}`}>
                        {field.label}
                      </span>
                    </label>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {!exportSuccess && (
        <div className="px-6 py-4 border-t border-(--color-border-gray) flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || selectedFieldCount === 0 || memberCount === 0}
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Export {format.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ExportGroupMembersModal;

